/**
 * @fileoverview Servicio para acceder a datos INE desde Cloudflare R2
 * @description Carga, cachea e indexa municipios de Andalucía desde INE R2
 * @author PTEL Tool - MapWizard
 * @version 1.0.0
 * @date 2026-02-02
 */

import type {
  INEMunicipio,
  INEMunicipioCollection,
  INER2LookupResult,
  INER2ServiceStats,
  INER2ServiceConfig,
  INECentroide,
} from '../../../types/ineR2';
import { isCodigoProvincialAndalucia } from '../../../types/ineR2';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

/**
 * URL base del Worker R2 (alias local).
 *
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 * Se preserva el nombre interno `R2_BASE_URL` para minimizar churn.
 */
const R2_BASE_URL = WORKER_BASE;

/** Endpoint para datos INE */
const INE_ENDPOINT = '/ine/municipios-andalucia.json';

/** Timeout por defecto para fetch (10s - archivo pequeño 195KB) */
const DEFAULT_TIMEOUT = 10000;

/**
 * Servicio singleton para acceder a datos INE R2
 * Proporciona lookups eficientes de municipios andaluces
 */
class INER2Service {
  private static instance: INER2Service | null = null;

  /** Mapa principal: codMun → INEMunicipio */
  private municipios: Map<string, INEMunicipio> = new Map();
  
  /** Índice por nombre normalizado: nombreNorm → codMun */
  private nombreIndex: Map<string, string> = new Map();
  
  /** Índice por provincia: codProv → codMun[] */
  private provinciaIndex: Map<string, string[]> = new Map();
  
  private initialized = false;
  private loading: Promise<boolean> | null = null;
  private loadedAt: number | null = null;
  private lastError: string | null = null;
  private config: Required<INER2ServiceConfig>;

  private constructor(config?: Partial<INER2ServiceConfig>) {
    this.config = {
      baseUrl: config?.baseUrl ?? R2_BASE_URL,
      timeout: config?.timeout ?? DEFAULT_TIMEOUT,
      debug: config?.debug ?? false,
    };
  }

  /**
   * Obtiene la instancia singleton del servicio
   */
  static getInstance(config?: Partial<INER2ServiceConfig>): INER2Service {
    if (!INER2Service.instance) {
      INER2Service.instance = new INER2Service(config);
    }
    return INER2Service.instance;
  }

  /**
   * Resetea la instancia singleton (para tests)
   */
  static resetInstance(): void {
    if (INER2Service.instance) {
      INER2Service.instance.municipios.clear();
      INER2Service.instance.nombreIndex.clear();
      INER2Service.instance.provinciaIndex.clear();
      INER2Service.instance.initialized = false;
      INER2Service.instance.loading = null;
      INER2Service.instance.loadedAt = null;
      INER2Service.instance.lastError = null;
    }
    INER2Service.instance = null;
  }

  /**
   * Inicializa el servicio cargando datos desde R2
   * @returns true si la carga fue exitosa
   */
  async initialize(): Promise<boolean> {
    // Si ya está inicializado, retornar éxito
    if (this.initialized && this.municipios.size > 0) {
      this.log('Servicio ya inicializado');
      return true;
    }

    // Si hay carga en progreso, esperar
    if (this.loading) {
      this.log('Carga en progreso, esperando...');
      return this.loading;
    }

    // Iniciar nueva carga
    this.loading = this.loadData();

    try {
      const result = await this.loading;
      return result;
    } finally {
      this.loading = null;
    }
  }

  /**
   * Carga datos internamente desde R2
   */
  private async loadData(): Promise<boolean> {
    try {
      this.lastError = null;
      this.log('Iniciando carga INE desde R2...');

      const url = `${this.config.baseUrl}${INE_ENDPOINT}`;
      this.log(`Fetching: ${url}`);

      const { response } = await fetchWithBackoff(url, {
        timeoutMs: this.config.timeout,
        headers: { 'Accept': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json() as INEMunicipioCollection;

      if (!data.municipios || !Array.isArray(data.municipios)) {
        throw new Error('Formato de datos INE inválido: falta array municipios');
      }

      // Construir índices
      this.buildIndexes(data.municipios);

      this.initialized = true;
      this.loadedAt = Date.now();
      this.log(`INE cargado: ${this.municipios.size} municipios`);

      return true;

    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.lastError = `Error cargando INE R2: ${message}`;
      console.error('[INER2Service]', this.lastError);
      return false;
    }
  }

  /**
   * Construye los índices para búsquedas rápidas
   */
  private buildIndexes(municipios: INEMunicipio[]): void {
    this.municipios.clear();
    this.nombreIndex.clear();
    this.provinciaIndex.clear();

    for (const m of municipios) {
      // Índice principal por código
      this.municipios.set(m.codMun, m);

      // Índice por nombre normalizado
      this.nombreIndex.set(m.nombreNorm, m.codMun);

      // Índice por provincia
      const provList = this.provinciaIndex.get(m.codProv) || [];
      provList.push(m.codMun);
      this.provinciaIndex.set(m.codProv, provList);
    }
  }

  // === MÉTODOS DE LOOKUP ===

  /**
   * Obtiene municipio por código INE
   * @param codMun Código INE 5 dígitos (ej: "04001")
   */
  getByCodMun(codMun: string): INEMunicipio | null {
    if (!this.initialized) {
      this.warnNotInitialized();
      return null;
    }
    return this.municipios.get(codMun) || null;
  }

  /**
   * Obtiene municipio por nombre
   * Busca primero por nombre normalizado exacto,
   * luego por coincidencia parcial ignorando acentos/mayúsculas
   * @param nombre Nombre del municipio
   */
  getByNombre(nombre: string): INEMunicipio | null {
    if (!this.initialized) {
      this.warnNotInitialized();
      return null;
    }

    // Normalizar entrada
    const normalized = this.normalizeNombre(nombre);

    // Búsqueda exacta
    const codMun = this.nombreIndex.get(normalized);
    if (codMun) {
      return this.municipios.get(codMun) || null;
    }

    // Búsqueda parcial (contiene)
    for (const [nombreNorm, code] of this.nombreIndex.entries()) {
      if (nombreNorm.includes(normalized) || normalized.includes(nombreNorm)) {
        return this.municipios.get(code) || null;
      }
    }

    return null;
  }

  /**
   * Obtiene centroide de un municipio
   * @param codMun Código INE 5 dígitos
   */
  getCentroide(codMun: string): INECentroide | null {
    const m = this.getByCodMun(codMun);
    if (!m) return null;
    return { x: m.centroideX, y: m.centroideY };
  }

  /**
   * Obtiene nombre de provincia por código municipal
   * @param codMun Código INE 5 dígitos
   */
  getProvincia(codMun: string): string | null {
    const m = this.getByCodMun(codMun);
    return m?.provincia || null;
  }

  /**
   * Obtiene todos los municipios de una provincia
   * @param codProv Código provincial 2 dígitos (ej: "04")
   */
  getMunicipiosByProvincia(codProv: string): INEMunicipio[] {
    if (!this.initialized) {
      this.warnNotInitialized();
      return [];
    }

    const codigos = this.provinciaIndex.get(codProv) || [];
    return codigos
      .map(cod => this.municipios.get(cod))
      .filter((m): m is INEMunicipio => m !== undefined);
  }

  // === MÉTODOS DE VALIDACIÓN ===

  /**
   * Valida si un código INE existe en la base de datos
   * @param codMun Código INE a validar
   */
  isValidCodMun(codMun: string): boolean {
    if (!this.initialized) {
      this.warnNotInitialized();
      return false;
    }
    return this.municipios.has(codMun);
  }

  /**
   * Valida si un código INE pertenece a Andalucía
   * @param codMun Código INE a validar
   */
  isAndalucia(codMun: string): boolean {
    if (!codMun || codMun.length < 2) return false;
    const codProv = codMun.substring(0, 2);
    return isCodigoProvincialAndalucia(codProv);
  }

  /**
   * Búsqueda completa con resultado estructurado
   * @param codMun Código INE
   */
  lookup(codMun: string): INER2LookupResult {
    const municipio = this.getByCodMun(codMun);
    return {
      found: municipio !== null,
      municipio: municipio ?? undefined,
    };
  }

  // === MÉTODOS DE ESTADÍSTICAS ===

  /**
   * Obtiene estadísticas del servicio
   */
  getStats(): INER2ServiceStats {
    return {
      count: this.municipios.size,
      initialized: this.initialized,
      loadedAt: this.loadedAt ?? undefined,
      error: this.lastError,
    };
  }

  /**
   * Obtiene todos los municipios cargados
   */
  getAllMunicipios(): INEMunicipio[] {
    return Array.from(this.municipios.values());
  }

  // === UTILIDADES PRIVADAS ===

  /**
   * Normaliza nombre: mayúsculas, sin acentos, sin espacios extra
   */
  private normalizeNombre(nombre: string): string {
    return nombre
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Eliminar diacríticos
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Warning cuando se usa el servicio sin inicializar
   */
  private warnNotInitialized(): void {
    console.warn('[INER2Service] Servicio no inicializado. Llamar a initialize() primero.');
  }

  /**
   * Log condicional según configuración
   */
  private log(message: string): void {
    if (this.config.debug) {
      console.log(`[INER2Service] ${message}`);
    }
  }
}

// === EXPORTS ===

/**
 * Obtiene la instancia singleton del servicio INER2
 */
export function getINER2Service(config?: Partial<INER2ServiceConfig>): INER2Service {
  return INER2Service.getInstance(config);
}

/**
 * Resetea la instancia singleton (para tests)
 */
export function resetINER2Service(): void {
  INER2Service.resetInstance();
}

export { INER2Service };
export default INER2Service;
